// noinspection SpellCheckingInspection

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import BookmarkedIndividual from "marketplace/entities/bookmarked-individual/bookmarked-individual";
import Individual from "marketplace/entities/individual/individual";
import { BookmarkedIndividuals } from "marketplace/view/bookmarked-individuals";
import React from "react";
import NetworkedIndividuals from "users/entities/user-network-connection/view/components/networked-individuals";
import Marketplace from "marketplace/values/team-profile/view/marketplace";
import NetworkedUserInfo from "users/entities/user-network-connection/networked-user-info";
import { NavigateFunction } from "react-router";
import { SetURLSearchParams } from "react-router-dom";
import Guid from "common/values/guid/guid";
import UserCoworkers from "users/values/user-coworker/view/user-coworkers";
import UserCoworker from "users/values/user-coworker/user-coworker";

const PageContainer = styled(Box)(() => ({
  display: 'grid',
  gridTemplateRows: "auto 1fr",
  width: "100%"
}));
const TabsContainer = styled("div")(() => ({
  gridRowStart: 1
}));
const TabContent = styled("div")(() => ({
  gridRowStart: 2,
  gridRowEnd: 'none'
}));

type TeamMemberSelectionProps = {
  selectLeader: boolean;
  selectMember: boolean;
  selectedTeamMemberUserIds: Guid[];
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onNewSelection: (teamMember: Individual) => void;
  popDialog: () => void;
};

export default function TeamMemberSelection(props: Readonly<TeamMemberSelectionProps>) {
  const {
    selectLeader,
    selectMember,
    selectedTeamMemberUserIds,
    navigate,
    searchParams,
    setSearchParams,
    onNewSelection,
    popDialog
  } = props;

  const [activeTab, setActiveTab] = React.useState("marketplace");

  function marketplaceIndividualSelected(individual: Individual) {
    onNewSelection(individual);
    popDialog();
  }

  function userCoworkerSelected(coworker: UserCoworker) {
      if (!coworker.individualId || !coworker.userId) {
        throw new Error("Coworker must have an individualId and userId");
      }
  
      onNewSelection(coworker.asIndividual());
      popDialog();
    }

  function bookmarkedIndividualSelected(individual: BookmarkedIndividual) {
    onNewSelection(individual.vendor);
    popDialog();
  }

  function networkedIndividualSelected(
    _connection: NetworkedUserInfo
  ) {
    throw new Error("Not implemented; profile requires more information");
  }

  return (
    <PageContainer>
      <TabsContainer>
        <Tabs
          indicatorColor="primary"
          variant="scrollable"
          value={activeTab}
          onChange={(event, tab) => setActiveTab(tab)}
          aria-label="Attorney selection nav">
          <Tab value="marketplace" label="Marketplace" />
          <Tab value="coworkers" label="Coworkers" />
          <Tab value="network" label="Network" />
          <Tab value="indvBookmarks" label="Bookmarked Individuals" />
        </Tabs>
      </TabsContainer>
      {activeTab === "network" && (
        <TabContent>
          <NetworkedIndividuals 
            selectedMemberUserIds={selectedTeamMemberUserIds}
            onSelected={networkedIndividualSelected} 
          />
        </TabContent>
      )}
      {activeTab === "indvBookmarks" && (
        <TabContent>
          <BookmarkedIndividuals 
            selectedMemberUserIds={selectedTeamMemberUserIds} 
            onSelected={bookmarkedIndividualSelected} 
          />
        </TabContent>
      )}
      {activeTab === "marketplace" && (
        <TabContent>
          <Marketplace
            navigate={navigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            selectingLeader={selectLeader}
            selectingMember={selectMember}
            selectedTeamMemberUserIds={selectedTeamMemberUserIds}
            onTeamMemberSelected={marketplaceIndividualSelected}
          />
        </TabContent>
      )}
      {activeTab === "coworkers" && (
        <TabContent>
          <UserCoworkers
            selectedMemberUserIds={selectedTeamMemberUserIds}
            onSelected={userCoworkerSelected}
          />
        </TabContent>
      )}
    </PageContainer>
  );
}
